/**
 * PPM Grade Thresholds hook - CR-6652
 *
 * Owns CRUD for the configurable PPM colour bands. Kept separate from
 * `usePerformanceSettings` so a PPM band edit never round-trips the whole
 * settings form — and can never write an OTIF grade by accident.
 */
import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { performanceSettingsAPI, PPMGradeThreshold, CreatePPMGradeThresholdData } from '../../../lib/api';
import { getApiErrorMessage } from '../../../utils/errorUtils';
import { useConfirm, useAlertDialog } from '../../../contexts/ConfirmDialogContext';

/** Editable shape of a band while its inline form is open. */
export interface PPMGradeEditData {
  grade: string;
  label: string;
  min_ppm: string;
  max_ppm: string;
  color: string;
  description: string;
  order: number;
}

const BLANK_BAND: CreatePPMGradeThresholdData = {
  grade: '',
  label: '',
  min_ppm: '0',
  max_ppm: '1000000',
  color: '#3B82F6',
  description: '',
  order: 0,
};

/**
 * Manage the PPM colour grade bands for the company being configured.
 *
 * @param bands - Bands from the loaded performance settings payload
 * @param onChanged - Called after any successful mutation so the parent can refetch
 * @param qualityLabel - "PPM" or "PPK" based on context setting
 * @returns Band edit state plus handlers for edit/add/delete/reset
 */
export function usePPMGradeThresholds(
  bands: PPMGradeThreshold[],
  onChanged: () => Promise<void> | void,
  qualityLabel: string = 'PPM',
) {
  const confirm = useConfirm();
  // CR-7271: a refused band write is explained in a modal the user must
  // dismiss, not a toast that slides away mid-sentence.
  const alertDialog = useAlertDialog();

  const [editingBand, setEditingBand] = useState<number | null>(null);
  const [bandEditData, setBandEditData] = useState<PPMGradeEditData | null>(null);
  const [savingBand, setSavingBand] = useState(false);
  const [showAddBand, setShowAddBand] = useState(false);
  const [newBandData, setNewBandData] = useState<CreatePPMGradeThresholdData>({ ...BLANK_BAND });
  const [addingBand, setAddingBand] = useState(false);
  const [resettingBands, setResettingBands] = useState(false);

  const handleEditBand = useCallback((band: PPMGradeThreshold) => {
    setEditingBand(band.id);
    setBandEditData({
      grade: band.grade,
      label: band.label,
      min_ppm: band.min_ppm,
      max_ppm: band.max_ppm,
      color: band.color,
      description: band.description,
      order: band.order,
    });
  }, []);

  const handleCancelEditBand = useCallback(() => {
    setEditingBand(null);
    setBandEditData(null);
  }, []);

  const handleBandFieldChange = useCallback((field: string, value: string | number) => {
    setBandEditData(prev => (prev ? { ...prev, [field]: value } : null));
  }, []);

  const handleSaveBand = useCallback(async (bandId: number) => {
    if (!bandEditData) return;

    try {
      setSavingBand(true);
      await performanceSettingsAPI.updatePPMGradeThreshold(bandId, {
        grade: bandEditData.grade,
        label: bandEditData.label,
        min_ppm: bandEditData.min_ppm,
        max_ppm: bandEditData.max_ppm,
        color: bandEditData.color,
        description: bandEditData.description,
        order: bandEditData.order,
      });
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success(`${qualityLabel} grade updated successfully`);
    } catch (error: unknown) {
      console.error(`Failed to update ${qualityLabel} grade:`, error);
      await alertDialog({ title: `${qualityLabel} grade not saved`, description: getApiErrorMessage(error, `Failed to update ${qualityLabel} grade`) });
    } finally {
      setSavingBand(false);
    }
  }, [bandEditData, onChanged, qualityLabel, alertDialog]);

  const handleNewBandFieldChange = useCallback((field: string, value: string | number) => {
    setNewBandData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAddBand = useCallback(async () => {
    if (!newBandData.grade.trim()) {
      toast.error('Please enter a grade identifier');
      return;
    }

    try {
      setAddingBand(true);
      const maxOrder = bands.reduce((max, b) => Math.max(max, b.order), 0);
      await performanceSettingsAPI.createPPMGradeThreshold({
        ...newBandData,
        order: maxOrder + 1,
      });
      await onChanged();
      setShowAddBand(false);
      setNewBandData({ ...BLANK_BAND });
      toast.success(`New ${qualityLabel} grade added successfully`);
    } catch (error: unknown) {
      console.error(`Failed to add ${qualityLabel} grade:`, error);
      await alertDialog({ title: `${qualityLabel} grade not added`, description: getApiErrorMessage(error, `Failed to add ${qualityLabel} grade`) });
    } finally {
      setAddingBand(false);
    }
  }, [newBandData, bands, onChanged, qualityLabel, alertDialog]);

  const handleDeleteBand = useCallback(async (bandId: number, label: string) => {
    const ok = await confirm({
      title: `Delete ${qualityLabel} Grade`,
      description: `Are you sure you want to delete the "${label}" ${qualityLabel} grade? Dashboards will stop using its colour.`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      await performanceSettingsAPI.deletePPMGradeThreshold(bandId);
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success(`${qualityLabel} grade deleted successfully`);
    } catch (error: unknown) {
      console.error(`Failed to delete ${qualityLabel} grade:`, error);
      await alertDialog({ title: `${qualityLabel} grade not deleted`, description: getApiErrorMessage(error, `Failed to delete ${qualityLabel} grade`) });
    }
  }, [confirm, onChanged, qualityLabel, alertDialog]);

  const handleResetBands = useCallback(async () => {
    const ok = await confirm({
      title: `Reset ${qualityLabel} Grades`,
      description: `This replaces every ${qualityLabel} grade with the default set. OTIF Grade Thresholds and Supplier Grade Thresholds are not affected.`,
      confirmLabel: 'Reset',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      setResettingBands(true);
      await performanceSettingsAPI.resetPPMGradeThresholds();
      await onChanged();
      setEditingBand(null);
      setBandEditData(null);
      toast.success(`${qualityLabel} grades reset to defaults`);
    } catch (error: unknown) {
      console.error(`Failed to reset ${qualityLabel} grades:`, error);
      await alertDialog({ title: `${qualityLabel} grades not reset`, description: getApiErrorMessage(error, `Failed to reset ${qualityLabel} grades`) });
    } finally {
      setResettingBands(false);
    }
  }, [confirm, onChanged, qualityLabel, alertDialog]);

  return {
    editingBand,
    bandEditData,
    savingBand,
    showAddBand,
    newBandData,
    addingBand,
    resettingBands,
    setShowAddBand,
    handleEditBand,
    handleCancelEditBand,
    handleBandFieldChange,
    handleSaveBand,
    handleAddBand,
    handleDeleteBand,
    handleResetBands,
    handleNewBandFieldChange,
  };
}
